import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_PANELS, ITEMS, findPanelByPath } from "../config/panels";

const STORAGE_KEY = "margevio-panels-v1";

const PanelsContext = createContext(null);

/**
 * Merges stored panels with DEFAULT_PANELS so newly introduced
 * items never vanish when users have an older localStorage entry.
 */
const mergeWithDefaults = (stored) => {
  if (!Array.isArray(stored) || stored.length === 0) return DEFAULT_PANELS;

  const allAssigned = new Set();
  const normalised = stored.map((p) => {
    const items = (p.items || []).filter((id) => ITEMS[id]);
    items.forEach((id) => allAssigned.add(id));
    return { ...p, items };
  });

  // Any item that exists in ITEMS but is not in any panel → append to its default panel
  Object.keys(ITEMS).forEach((id) => {
    if (allAssigned.has(id)) return;
    const defaultPanel = DEFAULT_PANELS.find((p) => p.items.includes(id));
    if (!defaultPanel) return;
    const target = normalised.find((p) => p.id === defaultPanel.id);
    if (target) target.items.push(id);
    else normalised.push({ ...defaultPanel });
  });

  return normalised;
};

export const PanelsProvider = ({ children }) => {
  const [panels, setPanelsState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return mergeWithDefaults(JSON.parse(raw));
    } catch (_) { /* noop */ }
    return DEFAULT_PANELS;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(panels));
  }, [panels]);

  const setPanels = useCallback((next) => setPanelsState(next), []);
  const resetPanels = useCallback(() => setPanelsState(DEFAULT_PANELS), []);

  /** Move an item from one panel to another. */
  const movePanelItem = useCallback((itemId, toPanelId) => {
    setPanelsState((prev) =>
      prev.map((p) => {
        const filtered = p.items.filter((i) => i !== itemId);
        if (p.id === toPanelId) return { ...p, items: [...filtered, itemId] };
        return { ...p, items: filtered };
      })
    );
  }, []);

  return (
    <PanelsContext.Provider value={{ panels, setPanels, resetPanels, movePanelItem }}>
      {children}
    </PanelsContext.Provider>
  );
};

export const usePanels = () => {
  const ctx = useContext(PanelsContext);
  if (!ctx) throw new Error("usePanels must be used within PanelsProvider");
  return ctx;
};

/** Returns the panel that matches the current URL or null when on /dashboard. */
export const useActivePanel = () => {
  const { panels } = usePanels();
  const { pathname } = useLocation();
  return findPanelByPath(panels, pathname);
};
